import { Badge } from '@shared/components/ui/badge';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { EnterIcon, EscIcon } from '@shared/components/ui/icons';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Switch } from '@shared/components/ui/shadcn/switch';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCreatePackingStation } from '../../hooks/useCreatePackingStation';
import { usePackingStationDetail } from '../../hooks/usePackingStationDetail';
import type { PackingStation } from '../../models/PackingStation';
import { PackingStationRelationsPanel } from './PackingStationRelationsPanel';

export interface CreatePackingStationModalRef {
  openCreate: () => void;
  openEdit: (station: PackingStation) => void;
}

type Step = 1 | 2;
type ModalMode = 'create' | 'edit';

export const CreatePackingStationModal = forwardRef<CreatePackingStationModalRef, { onSuccess?: () => void }>(
  ({ onSuccess }, ref) => {
    const { t } = useTranslation('packingStationsModule');
    const modalRef = useRef<ModalCenterRef>(null);

    const [mode, setMode] = useState<ModalMode>('create');
    const [step, setStep] = useState<Step>(1);
    const [createdId, setCreatedId] = useState<number | null>(null);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editSnapshot, setEditSnapshot] = useState<{ code: string; active: boolean } | null>(null);
    const [code, setCode] = useState('');
    const [active, setActive] = useState(true);

    const { createBareStation, updateStation, isCreating, isUpdating } = useCreatePackingStation();
    const { detail, isLoading, refetch } = usePackingStationDetail(step === 2 && createdId !== null ? createdId : null);

    const isSubmitting = isCreating || isUpdating;

    const resetForm = () => {
      setMode('create');
      setStep(1);
      setCreatedId(null);
      setEditingId(null);
      setEditSnapshot(null);
      setCode('');
      setActive(true);
    };

    useImperativeHandle(ref, () => ({
      openCreate: () => {
        resetForm();
        setMode('create');
        modalRef.current?.openModal();
      },
      openEdit: (station: PackingStation) => {
        setMode('edit');
        setStep(1);
        setCreatedId(null);
        setEditingId(station.id);
        const initialCode = station.code ?? '';
        const initialActive = Boolean(station.active);
        setEditSnapshot({ code: initialCode, active: initialActive });
        setCode(initialCode);
        setActive(initialActive);
        modalRef.current?.openModal();
      },
    }));

    const handleStep1Submit = async () => {
      if (!code.trim()) return;

      if (mode === 'edit' && editingId !== null && editSnapshot) {
        const ok = await updateStation(editingId, { code: code.trim(), active }, editSnapshot);
        if (!ok) return;
        modalRef.current?.closeModal();
        resetForm();
        onSuccess?.();
        return;
      }

      const station = await createBareStation({
        code: code.trim(),
        active,
      });
      if (!station) return;
      setCreatedId(station.id);
      setStep(2);
      onSuccess?.();
    };

    const handleInvalidateAfterRelations = () => {
      void refetch();
      onSuccess?.();
    };

    const displayCode = detail?.code ?? code;
    const displayActive = detail?.active ?? active;

    const modalTitle =
      step === 1 ? (
        mode === 'edit' ? (
          t('form.editTitle')
        ) : (
          t('form.title')
        )
      ) : (
        <div className="flex items-center gap-3">
          <span className="font-mono font-semibold">{displayCode}</span>
          {displayActive ? (
            <Badge variant="default" className="text-xs bg-bg-success text-success border-0">
              {t('table.status.active')}
            </Badge>
          ) : (
            <Badge variant="outline" className="text-xs text-title">
              {t('table.status.inactive')}
            </Badge>
          )}
        </div>
      );

    const showStep2 = mode === 'create' && step === 2 && createdId !== null;

    return (
      <ModalCenter
        ref={modalRef}
        title={modalTitle}
        type="default"
        width={showStep2 ? 'xl' : 'lg'}
        onCloseModal={resetForm}
      >
        {step === 1 && (
          <div className="space-y-6" data-testid="modal-nuevo-puesto-de-trabajo-content">
            <div className="space-y-4">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-complementary-text">
                  {t('form.fields.code')} <span className="text-error">*</span>
                </label>
                <Input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder={t('form.fields.codePlaceholder')}
                  disabled={isSubmitting}
                  data-testid="packing-station-code-input"
                />
              </div>
              <div className="space-y-1.5">
                <div
                  className="cursor-pointer rounded-md border border-card-border bg-card-bg px-3 py-2"
                  onClick={() => {
                    if (!isSubmitting) setActive((prev) => !prev);
                  }}
                  onKeyDown={(e) => {
                    if (isSubmitting) return;
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setActive((prev) => !prev);
                    }
                  }}
                  role="button"
                  tabIndex={isSubmitting ? -1 : 0}
                  aria-disabled={isSubmitting}
                  data-testid="packing-station-status-container"
                >
                  <div className="flex items-center justify-between gap-3 my-[1px]">
                    <label htmlFor="packing-station-active-switch" className="text-xs font-medium text-title">
                      {t('form.fields.status')}
                    </label>
                    <div className="flex items-center gap-3">
                      <Switch
                        id="packing-station-active-switch"
                        checked={active}
                        onCheckedChange={setActive}
                        onClick={(e) => e.stopPropagation()}
                        disabled={isSubmitting}
                        data-testid="packing-station-status-switch"
                      />
                      <span className="text-xs text-complementary-text">
                        {active ? t('form.active.active') : t('form.active.inactive')}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t border-lines">
              <Button
                variant="outline"
                size="lg"
                onClick={() => modalRef.current?.closeModal()}
                disabled={isSubmitting}
                data-testid="cancel-create-packing-station"
              >
                {t('form.buttons.cancel')}
                <EscIcon className="w-4 h-4" />
              </Button>
              <Button
                variant="primary"
                size="lg"
                onClick={() => void handleStep1Submit()}
                disabled={!code.trim() || isSubmitting}
                data-testid="submit-create-packing-station"
              >
                {isSubmitting ? t('form.buttons.saving') : t('form.buttons.save')}
                <EnterIcon className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {showStep2 && (
          <PackingStationRelationsPanel
            stationId={createdId}
            detail={detail ?? null}
            isLoadingDetail={isLoading}
            onInvalidate={handleInvalidateAfterRelations}
          />
        )}
      </ModalCenter>
    );
  },
);

CreatePackingStationModal.displayName = 'CreatePackingStationModal';
